import React, { useState } from "react";
import {
  Heart,
  Globe2,
  GraduationCap,
  Calculator,
  ClipboardList,
  Scale,
  FolderCheck,
  Cpu,
  Brain,
  Zap,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";

const CATEGORIES = [
  {
    n: "01",
    accent: "green",
    icon: Heart,
    title: "Welfare Benefits & Administrative Support",
    items: ["Universal Credit administrative support", "PIP & DLA form support", "Child Benefit administration", "Carer's Allowance administration", "Council Tax Support", "Pension Credit administrative assistance", "Mandatory Reconsideration document preparation", "Supporting letters", "Evidence organisation", "Journal message assistance", "Form completion support", "Appointment preparation", "DWP administrative correspondence"],
    outcome: "Helping you understand the process and prepare your documentation. Eligibility and entitlement decisions are made exclusively by the relevant government authority.",
  },
  {
    n: "02",
    accent: "navy",
    icon: Globe2,
    title: "Immigration Document & Digital Status Administrative Support",
    items: ["Document organisation", "GOV.UK digital services access", "Share Code", "Residency documentation", "Consular appointments", "Information preparation for EUSS / ETA submissions"],
    outcome: "Administrative assistance only — not regulated immigration advice. Where regulated advice is required, please consult an authorised immigration adviser or solicitor.",
  },
  {
    n: "03",
    accent: "purple",
    icon: GraduationCap,
    title: "Education & Student Support",
    items: ["Student Finance England applications", "Evidence preparation", "Application forms", "University application administration", "Related documentation"],
    outcome: "Supporting students and families with education paperwork. References to institutions do not imply endorsement or partnership.",
  },
  {
    n: "04",
    accent: "yellow",
    icon: Calculator,
    title: "Accounting, Bookkeeping & Business Administration",
    items: ["Bookkeeping", "Financial record organisation", "Payroll administration", "Payslip preparation", "Business administration", "Invoice preparation", "Expense record organisation", "Self Assessment administrative support", "Tax documentation preparation", "HMRC administrative support"],
    outcome: "Accurate, organised and reliable financial administration. We do not guarantee tax refunds, HMRC decisions or specific financial outcomes.",
  },
  {
    n: "05",
    accent: "orange",
    icon: ClipboardList,
    title: "Administrative Support Services",
    items: ["Form Filling", "Online Applications", "Document Preparation", "Eligibility Information", "Official Letters", "General Admin Assistance"],
    outcome: "We handle the paperwork so you can focus on what matters most.",
  },
  {
    n: "06",
    accent: "red",
    icon: Scale,
    title: "Administrative Case & Dispute Support",
    items: ["Correspondence preparation", "Evidence organisation", "Administrative letter drafting", "Mandatory Reconsideration document preparation", "Understanding procedural correspondence", "Housing & rent administrative matters"],
    outcome: "Assistance with preparing correspondence, organising evidence and understanding procedural correspondence. Marioway is not a law firm and does not undertake reserved legal activities.",
  },
  {
    n: "07",
    accent: "brown",
    icon: FolderCheck,
    title: "Documents & Application Services",
    items: ["DBS Applications", "Driving Licence Applications", "Birth Certificates", "UK Documents", "Romanian Consular Document Support", "Consulate Appointments"],
    outcome: "Organised, reliable and professional document administration.",
  },
  {
    n: "08",
    accent: "navy",
    icon: Cpu,
    title: "Digital & Smart Tools",
    badge: "MODERN BUSINESS",
    items: ["Sage Accounting", "Copilot AI", "Invoice2Go", "RocketLawyer", "MTD – Making Tax Digital"],
    outcome: "Modern tools for a smarter, faster and more efficient service.",
  },
  {
    n: "09",
    accent: "navy",
    icon: Brain,
    title: "Consultancy & Guidance",
    badge: "CORE VALUE",
    items: ["UK System Navigation", "Problem Solving", "Personalised Case Guidance", "Support for Individuals & Families", "Multilingual (EN / RO / ES)"],
    outcome: "Individual support based on your circumstances. Practical solutions. Long-term support.",
  },
  {
    n: "10",
    accent: "yellow",
    icon: Zap,
    title: "Fast & Easy Services",
    items: ["Fast Applications", "Document Checking", "Quick Form Completion", "Express Support", "Priority Assistance"],
    outcome: "Quick, simple and convenient services when you need them most.",
  },
];

export const ServicesCategories = () => {
  const [active, setActive] = useState(null);
  return (
    <section id="services" className="section bg-[#F5F7FA]">
      <div className="container-x">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="eyebrow"><ShieldCheck size={12} /> 10 Core Services</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-5 text-[#002B7F] leading-[1.05]">
            Complete support <span className="text-[#F37021]">under one roof.</span>
          </h2>
          <p className="mt-5 text-[#4A4A4A] text-base max-w-2xl mx-auto">
            From welfare benefits to immigration documentation, education to accounting administration, paperwork to administrative disputes — practical support under one roof.
            Personalised, multilingual support delivered with care.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
            {["EN", "RO", "ES"].map((l) => (
              <span key={l} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E6EAF0] text-[12px] font-bold tracking-[0.14em] text-[#002B7F]" data-testid={`lang-${l}`}>
                <Globe2 size={12} className="text-[#F37021]" /> {l}
              </span>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {CATEGORIES.map((cat, idx) => {
            const Icon = cat.icon;
            const isActive = active === idx;
            return (
              <article
                key={cat.n}
                className={`category-card accent-${cat.accent} ${isActive ? "is-active" : ""}`}
                onMouseEnter={() => setActive(idx)}
                onMouseLeave={() => setActive(null)}
                data-testid={`category-card-${idx}`}
              >
                <div className="cat-num">{cat.n}</div>
                <div className="cat-icon"><Icon size={22} /></div>
                <div className="cat-body">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="cat-title">{cat.title}</h3>
                    {cat.badge && <span className="cat-badge">{cat.badge}</span>}
                  </div>
                  <p className="cat-items">{cat.items.join(" · ")}</p>
                  <div className="hair my-3" />
                  <p className="cat-outcome"><CheckCircle2 size={14} /> {cat.outcome}</p>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-10 rounded-2xl border border-[#E6EAF0] bg-white p-5 md:p-6 text-[13px] text-[#4A4A4A] leading-relaxed" data-testid="categories-compliance-note">
          <p>
            <span className="font-bold text-[#002B7F]">Independent organisation.</span> Marioway Benefits Consultancy Ltd is an independent organisation and is not affiliated with or endorsed by the Department for Work and Pensions, HMRC, the Home Office, local authorities or any other government body.
          </p>
          <p className="mt-2">
            Eligibility and entitlement decisions are made exclusively by the relevant government authority. Marioway is not a law firm and is not currently presented as an Immigration Advice Authority-regulated immigration adviser.
          </p>
        </div>

        <div className="mt-10 text-center">
          <a href="/#contact" className="btn-orange" data-testid="categories-cta">
            Get personalised support <ArrowRight size={18} />
          </a>
          <div className="mt-4 text-[13px] text-[#4A4A4A]">
            <span className="font-bold text-[#CE1126]">YOUR SUPPORT.</span>{" "}
            <span className="font-bold text-[#F37021]">OUR PRIORITY.</span>
          </div>
        </div>
      </div>
    </section>
  );
};
